import { useState, useRef, useEffect } from 'react';
import { Spinner } from './ui/Spinner';
import { Markdown } from './ui/Markdown';
import { useFileUpload } from '../hooks/useFileUpload';
import type { ChatMessage, FileAttachment } from '@pantheon/shared';

export interface ChatPanelProps {
  messages: ChatMessage[];
  onSend: (message: string, attachments?: FileAttachment[]) => void;
  loading: boolean;
  disabled: boolean;
  agentName?: string;
  className?: string;
}

const FILE_ICONS: Record<string, string> = {
  'image/': '\uD83D\uDDBC\uFE0F',
  'application/pdf': '\uD83D\uDCC4',
  'text/': '\uD83D\uDCC3',
  default: '\uD83D\uDCCE',
};

function fileIcon(mimeType: string): string {
  for (const [prefix, icon] of Object.entries(FILE_ICONS)) {
    if (prefix !== 'default' && mimeType.startsWith(prefix)) return icon;
  }
  return FILE_ICONS.default;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function ChatPanel({ messages, onSend, loading, disabled, agentName = 'Hermes', className = '' }: ChatPanelProps) {
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFiles, uploading } = useFileUpload();

  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll within the chat container only — never the page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && pendingFiles.length === 0) || loading || disabled) return;

    let attachments: FileAttachment[] | undefined;
    if (pendingFiles.length > 0) {
      attachments = await uploadFiles(pendingFiles);
      setPendingFiles([]);
    }

    onSend(input.trim() || 'See attached files', attachments);
    setInput('');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isProcessing = loading || uploading;

  return (
    <div className={`flex flex-col bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 h-96 ${className}`}>
      {/* Messages */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && !loading && (
          <p className="text-gray-500 text-center mt-12 text-sm">
            Review the tickets below, then tell {agentName} what to change.
          </p>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            {msg.sender && (
              <span className="text-xs text-gray-500 mb-0.5 px-2">{msg.sender}</span>
            )}
            <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'bg-primary text-white rounded-2xl rounded-br-sm'
                : 'bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm'
            }`}>
              {msg.role === 'user' ? msg.content : <Markdown>{msg.content}</Markdown>}
            </div>
            {msg.attachments && msg.attachments.length > 0 && (
              <div className={`flex flex-wrap gap-1 mt-1 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.attachments.map((file, j) => (
                  <span key={j} className="text-xs bg-gray-200 text-gray-600 px-2.5 py-0.5 rounded-full">
                    {fileIcon(file.mimeType)} {file.fileName} ({formatSize(file.size)})
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
              <Spinner size="sm" className="text-gray-500" />
              <span className="text-sm text-gray-500">
                {uploading ? 'Uploading files...' : `${agentName} is thinking...`}
              </span>
            </div>
          </div>
        )}

      </div>

      {/* Pending files */}
      {pendingFiles.length > 0 && (
        <div className="px-3 pb-1 flex flex-wrap gap-1.5">
          {pendingFiles.map((file, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-xs bg-primary-light text-primary px-2.5 py-1 rounded-full">
              {fileIcon(file.type)} {file.name}
              <button onClick={() => removePendingFile(i)} className="hover:text-danger ml-0.5">&times;</button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 p-3 border-t border-gray-100">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt,.md,.csv,.xls,.xlsx"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing || disabled}
          className="p-2 rounded-full text-gray-500 hover:text-primary hover:bg-primary-light disabled:text-gray-400 transition-colors shrink-0"
          title="Attach files"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        </button>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={disabled ? 'Session is not in review' : `Tell ${agentName} what to change...`}
          disabled={isProcessing || disabled}
          className="flex-1 px-4 py-2.5 rounded-full border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={(!input.trim() && pendingFiles.length === 0) || isProcessing || disabled}
          className="px-5 py-2.5 rounded-full bg-primary text-white text-sm font-medium hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Send
        </button>
      </form>
    </div>
  );
}
